import { MoreHorizontal } from 'lucide-react';

import { CenterFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import Menu from '@/components/Menu/Menu';
import Typo from '@/components/Typography/Typo';
import service from '@/services/index';
import usePopupStore from '@/stores/pure/PopupStore';
import useToastStore, { useToast } from '@/stores/pure/ToastStore';
import { useRecentDashboardHistory } from '@/stores/WorkspaceCacheStore';
import { clipboardCopy } from '@/utils/util';
import { getCurrWorkspaceAlias } from '@/utils/workspace';

import { useInvalidateDashboardListQueries } from '../hooks/useDashboardListQuery';
import type { Dashboard2FolderT } from '../types';

type Props = {
  folder: Dashboard2FolderT;
  searchValue: string | null;
  onEditDashboardFolder?: (dashboardFolder: Dashboard2FolderT) => void;
};

const DashboardFolderAction = (props: Props) => {
  const addToast = useToast();
  const setPopup = usePopupStore((state) => state.setPopup);
  const addDefaultToast = useToastStore((state) => state.addDefaultToast);
  const { deleteHistory } = useRecentDashboardHistory();
  const invalidateDashboardLists = useInvalidateDashboardListQueries();

  const handleDelete = async () => {
    setPopup({
      popupTheme: 'negative',
      type: 'confirm',
      title: 'Delete folder',
      message: (
        <Typo type="body3">
          Are you sure you want to delete this folder?
          <br />
          This action will delete the folder but not the dashboard(s) in the
          folder.
        </Typo>
      ),
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: async () => {
        await service.dashboard2.deleteDashboardFolder(props.folder.alias);
        deleteHistory([props.folder.alias]);
        await invalidateDashboardLists();
        addToast('Dashboard Folder deleted', 'success');
      },
    });
  };

  const handleRename = () => {
    props.onEditDashboardFolder?.(props.folder);
  };

  const handleShare = () => {
    const wa = getCurrWorkspaceAlias();
    const newUrl = `${window.location.origin}/workspace/${wa}/dashboard2/list/folder/${props.folder.alias}`;
    clipboardCopy(newUrl);
    addDefaultToast('Dashboard link Copied', 'success');
  };

  return (
    <CenterFlex width="100%">
      <Menu
        triggerNode={
          <Button
            variant="ghost_secondary"
            sizeType="s"
            leftIcon={<MoreHorizontal />}
          />
        }
        options={[
          {
            label: 'Rename',
            onClick: handleRename,
          },
          {
            label: 'Share',
            onClick: handleShare,
          },
          {
            label: 'Delete',
            onClick: handleDelete,
          },
        ]}
      />
    </CenterFlex>
  );
};

export default DashboardFolderAction;
